import { Component, computed, DestroyRef, inject, input, viewChild } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormControl } from '@angular/forms';

import { startWith, switchMap } from 'rxjs';

import type { Attachment } from '@core/attachments-types';
import { AttachmentsListComponent } from '@core/components-attachments-list-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
import type { FileReadResult } from '@core/user-interface-legacy';
import {
  ButtonComponent,
  ButtonLoadingDirective,
  FileHandlerComponent,
  FileReadStatus,
  IconComponent,
} from '@core/user-interface-legacy';
import { EvaluationAttachmentsService } from '@recruitee/evaluations-logic';

@Component({
  selector: 'rt-evaluation-file-field',
  templateUrl: './evaluation-file-field.component.html',
  styleUrls: ['./evaluation-file-field.component.less'],
  imports: [
    ButtonComponent,
    ButtonLoadingDirective,
    IconComponent,
    FileHandlerComponent,
    AttachmentsListComponent,
    TranslatePipe,
  ],
})
export class EvaluationFileFieldComponent {
  private readonly attachmentsService = inject(EvaluationAttachmentsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly fileHandler = viewChild(FileHandlerComponent);
  private readonly uploadedById = new Map<number, Attachment>();

  public readonly attachmentIds = input.required<FormArray<FormControl<number>>>();
  public readonly existingAttachments = input<Attachment[]>([]);
  public readonly preview = input<boolean>(false);

  protected isUploading = this.attachmentsService.isUploading;

  protected attachmentIdsValue = toSignal(
    toObservable(this.attachmentIds).pipe(
      switchMap(control => control.valueChanges.pipe(startWith(control.value))),
    ),
    { initialValue: <number[]>[] },
  );

  protected files = computed(() => {
    const lookup = new Map(
      this.existingAttachments().map(attachment => [attachment.id, attachment]),
    );
    this.uploadedById.forEach((attachment, id) => lookup.set(id, attachment));

    return this.attachmentIdsValue()
      .map(id => lookup.get(id))
      .filter((attachment): attachment is Attachment => !!attachment);
  });

  protected openFilePicker(): void {
    this.fileHandler()?.instance.readFileFromDisk();
  }

  protected onNextFile(result: FileReadResult): void {
    if (result.status !== FileReadStatus.OK || !result.files) return;

    const files = result.files instanceof FileList ? Array.from(result.files) : [result.files];
    files.forEach(file => this.uploadFile(file));
  }

  protected onRemoveFile(attachment: Attachment): void {
    this.attachmentsService
      .delete(attachment.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.removeAttachment(attachment.id));
  }

  private uploadFile(file: File): void {
    this.attachmentsService
      .upload(file)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: attachment => this.addAttachment(attachment), error: () => undefined });
  }

  private addAttachment(attachment: Attachment): void {
    const attachmentIds = this.attachmentIds();
    this.uploadedById.set(attachment.id, attachment);
    attachmentIds.push(new FormControl(attachment.id, { nonNullable: true }));
    attachmentIds.markAsDirty();
    attachmentIds.markAsTouched();
  }

  private removeAttachment(id: number): void {
    const attachmentIds = this.attachmentIds();
    const index = attachmentIds.value.findIndex(value => value === id);

    if (index !== -1) {
      attachmentIds.removeAt(index);
      attachmentIds.markAsDirty();
    }
  }
}
